import { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { EditorContext } from "../containers/editor/EditorLayout";
import { TYPE_INFOGRAPHIC, TYPE_PROPOSAL } from "../constants/editor";
import { calculateNewZoomValue } from "../_helpers/utils";
import useAddPage from "./useAddPage";
import useFitToScreen from "./useFitToScreen";
import useDocumentParser from "./useDocumentParser";

const useDocumentReader = () => {
  const { start: initDocumentParser } = useDocumentParser();
  const { addPage } = useAddPage();

  const {
    updateWidgets,
    updatePageMargin,
    updateDimension,
    updateMetadata,
    setIsDocumentReady,
    documentType,
    isTimeLineViewOpen,
    updateGrid,
  } = useContext(EditorContext);

  const [onload, setOnload] = useState(true);
  const { fitOnScreenMethod } = useFitToScreen();

  const documentReader = {
    meta: false,

    updateContext: ({ pageNodes, blockNodes, widgets, backgroundColors, backgroundImages, ...meta }) => {
      updateWidgets(
        widgets,
        pageNodes,
        blockNodes,
        backgroundColors,
        backgroundImages,
        false,
        false,
        false,
        false,
        meta?.audios
      );

      updateDimension({
        ...meta?.dimension,
        zoom:
          documentType === TYPE_INFOGRAPHIC
            ? 100
            : calculateNewZoomValue(
                meta.dimension.width,
                documentType === TYPE_PROPOSAL ? 768 : meta.dimension.height,
                isTimeLineViewOpen,
                true // video enable user
              ),
      });
      updatePageMargin(meta?.margin ?? meta?.pageMargin);
      updateGrid(meta?.grid);

      updateMetadata({
        pageController: {
          style: {
            top: 0,
          },
        },
        activeOutSideCanvasArea: false,
        activePageId: pageNodes[0]?.pageId,
        activePageIdx: 0,
        activeBlockId: blockNodes[0]?.blockId,
        activeBlockIdx: 0,
        activeWidgetId: false,
        activeWidgetType: false,
        activeWidgetIdx: 0,
        pageCount: pageNodes?.length,
        blockCount: blockNodes?.length,
      });
    },

    getContext: async ({ data, returnObj = null }) => {
      // blank document (react js)
      if (!data?.version?.rawdata) {
        return returnObj;
      }

      // react js document
      if (data?.version?.rawdata?.contexts) {
        returnObj = data?.version?.rawdata?.contexts;

        return returnObj;
      }

      // angular js document
      if (!data?.version?.rawdata?.contexts) {
        returnObj = await initDocumentParser({ data });

        return returnObj;
      }
    },

    start: async ({ data }) => {
      const contextData = await documentReader.getContext({ data });

      if (contextData) {
        // for saved document (angular + react)
        documentReader.updateContext({ ...contextData });
      } else {
        // for blank document
        addPage();
      }

      setTimeout(() => {
        setIsDocumentReady(true);
        fitOnScreenMethod(onload, setOnload);
      }, 30);
    },
  };

  return { start: documentReader.start };
};

export default useDocumentReader;
